import type { CSSProperties } from "react";
import { WARM } from "@shared/brand";
import { useReportNoneAnswer } from "./useNoneAnswer";

/**
 * s221 Lane 1, mechanism 1 — THE TILE PICKER (SPEC: `Main.dc.html` `steps()`, `groups[].tiles` + `none`).
 *
 * The first question on most interview screens: tap every tile that is true, or the one "none" tile.
 * Presentational only — it owns no state and writes nothing. The screen holds the selection and
 * decides what a pick writes (a row, a situation flag), so every screen keeps its writes in one
 * place and the single-flight guard's registry names the screen, not this file.
 *
 * Behaviour, from the SPEC board's `pick` handlers:
 *   • multi (the default): each tile toggles on its own; turning one on turns "none" off.
 *   • single: turning one on turns every other tile off.
 *   • `excl`: turning a tile on turns the named tiles off (a signed will and a draft cannot both be true).
 *   • "none": turning it on turns every tile off. It is the No answer, and its `note` says what it prunes.
 *
 * Look (s221 Lane 1b, D-221.28 — the board, exactly): a 12px tile at least 52px tall, a 32px mark, the
 * sub-line in the board's muted colour, a check mark when picked, the board's SEL / OFF colours, and a
 * grid of `repeat(cols, minmax(0, 380px))`.
 *
 * The reducers are pure and exported so the rules are tested without a DOM.
 */

export type Tile = {
  id: string;
  label: string;
  sub?: string;
  /** The short badge on the tile ("CHILD", "CARE"). */
  mark?: string;
  /** The badge colour. */
  color?: string;
  /** Tiles this one turns off when it is turned on. */
  excl?: string[];
};

export type NoneTile = {
  label: string;
  sub?: string;
  /** Shown once "none" is on — what the answer takes off the executor's list (§4B 10). */
  note?: string;
  /** s221 Lane 1d: how far the executor-task counter drops while this tile is picked (the board's
   *  `none.prune`). Declared in the step model, never typed into a screen. */
  prune?: number;
  /** s221 Lane 1d: the sentence the board shows while this tile is picked (`none.delta`). */
  delta?: string;
};

export type TileSelection = { selected: string[]; none: boolean };

/** s233: the two tile sizes — the fixed 80px (D-228.67, the default) and rule 153's 52px that grows for a sub-line. */
export const TILE_FIXED = "h-20 min-h-20";
export const TILE_COMPACT = "min-h-[52px]";

/** Toggle one tile. Pure. */
export function pickTile(
  state: TileSelection,
  tile: Pick<Tile, "id" | "excl">,
  mode: "multi" | "single" = "multi",
): TileSelection {
  const isOn = state.selected.includes(tile.id);
  if (isOn) return { selected: state.selected.filter((id) => id !== tile.id), none: state.none };
  const base = mode === "single" ? [] : state.selected.filter((id) => !(tile.excl ?? []).includes(id));
  return { selected: [...base, tile.id], none: false };
}

/** Toggle the "none" tile. Turning it on clears every tile; turning it off leaves the tiles empty. Pure. */
export function pickNone(state: TileSelection): TileSelection {
  return state.none ? { selected: state.selected, none: false } : { selected: [], none: true };
}

// The board's SEL / OFF (Main.dc.html): picked = stone-light fill, sage-600 border, sage-800 text.
const ON = "border-sage-600 bg-warm-stone-light text-sage-800";
const OFF = "border-warm-stone bg-warm-white text-warm-charcoal hover:border-sage-600";

const CHECK = (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" className="shrink-0 text-sage-800" data-check="true">
    <path d="M20 6 9 17l-5-5" />
  </svg>
);

const NONE_MARK = (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
    <circle cx="12" cy="12" r="9" />
    <path d="m5.6 5.6 12.8 12.8" />
  </svg>
);

export function TilePicker({
  tiles,
  selection,
  onChange,
  none,
  mode = "multi",
  columns = 3,
  testIdPrefix = "interview-tiles",
  compact = false,
}: {
  tiles: readonly Tile[];
  selection: TileSelection;
  /** Called with the next selection and the tile that changed (`"none"` for the none tile). */
  onChange: (next: TileSelection, changed: string) => void;
  none?: NoneTile;
  mode?: "multi" | "single";
  columns?: 2 | 3 | 4 | 5;
  testIdPrefix?: string;
  /**
   * s233 (rule 153, D-233.65; D-233.70(7)): tiles 52px tall, growing only for a sub-line — every tile in a row matches
   * the tallest (`auto-rows-fr`). Off by default: the fixed 80px (D-228.67) until a chapter turns it on.
   */
  compact?: boolean;
}) {
  // s221 Lane 1d: while "none" is picked, report what it declares — the shell drops the counter by the
  // declared prune and shows the declared sentence. Built ONCE here, so every screen inherits it.
  useReportNoneAnswer(testIdPrefix, selection.none,
    none && (none.prune !== undefined || none.delta) ? { prune: none.prune ?? 0, delta: none.delta } : undefined);
  // The board's grid, one column on a phone.
  const grid = { "--tile-cols": `repeat(${columns}, minmax(0, 380px))` } as CSSProperties;
  const size = compact ? TILE_COMPACT : TILE_FIXED;
  return (
    <div data-testid={`${testIdPrefix}-tiles`}>
      <div className="grid auto-rows-fr grid-cols-1 gap-2 sm:[grid-template-columns:var(--tile-cols)]" style={grid} role="group">
        {tiles.map((t) => {
          const on = selection.selected.includes(t.id);
          return (
            <button
              key={t.id}
              type="button"
              aria-pressed={on}
              onClick={() => onChange(pickTile(selection, t, mode), t.id)}
              className={`flex ${size} w-full items-center gap-2.5 rounded-[12px] border px-3 py-2.5 text-left ${on ? ON : OFF}`}
              data-testid={`${testIdPrefix}-tile-${t.id}`}
            >
              {t.mark && (
                <span
                  className="flex h-8 w-8 shrink-0 items-center justify-center rounded-[8px] text-[11px] font-medium tracking-[0.02em] text-warm-white"
                  style={{ backgroundColor: t.color ?? WARM.taupe }}
                >
                  {t.mark}
                </span>
              )}
              <span className="flex min-w-0 flex-grow flex-col gap-px">
                {/* s226 (Reade: "2/ agree"): the name WRAPS instead of being cut by the tick (Main.dc.html, the roomy
                    tile's label: line-height 1.25, text-wrap balance). */}
                <span className="text-[14px] leading-[1.25] [text-wrap:balance]" data-testid={`${testIdPrefix}-tile-label-${t.id}`}>{t.label}</span>
                {t.sub && <span className="text-[13px] text-warm-muted">{t.sub}</span>}
              </span>
              {on && CHECK}
            </button>
          );
        })}
        {/* s228 (Reade, staging walk item 3; board v349): the none tile is the NEXT tile in the grid, the same size as
            the others — never a row of its own. Its `sub` (only exec's carries one) sits under the tile row. */}
        {none && (
          <button
            type="button"
            aria-pressed={selection.none}
            onClick={() => onChange(pickNone(selection), "none")}
            className={`flex ${size} w-full items-center gap-2.5 rounded-[12px] border px-3 py-2.5 text-left ${selection.none ? ON : OFF}`}
            data-testid={`${testIdPrefix}-none`}
          >
            <span className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-[8px] text-warm-white ${selection.none ? "bg-sage-800" : "bg-warm-taupe"}`}>
              {NONE_MARK}
            </span>
            <span className="min-w-0 flex-grow text-[14px] leading-[1.25] [text-wrap:balance]">{none.label}</span>
            {selection.none && CHECK}
          </button>
        )}
      </div>
      {none?.sub && <p className="mt-2 text-[13px] text-warm-muted" data-testid={`${testIdPrefix}-none-sub`}>{none.sub}</p>}
      {none?.note && selection.none && (
        <p className="mt-3 text-[13px] text-warm-muted" data-testid={`${testIdPrefix}-none-note`}>
          {none.note}
        </p>
      )}
    </div>
  );
}
